import { GetAppSettings, GoFormzServices } from '../../AppSettings';
import { GetAuthReturnUrl, GetProductionAuthReturnUrl } from '../../AuthReturn';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import { apimCall } from '../../helpers/api';
import { showBanner } from '../../helpers/MessageUtility';
import { getPathInfo, getQuerystringParameters } from '../../helpers/utility';

export async function connectApp(app, authenticationOption, configValues, appInstanceId, onAddingConnection) {
  try {
    let authCode = null;

    if (authenticationOption && authenticationOption.type === 'oauth2') {
      authCode = await authorizeOAuth2Connection(app, authenticationOption.authInfo, configValues);
    }

    if (onAddingConnection) {
      onAddingConnection();
    }

    const response = await apimCall(GoFormzServices.ApplicationService, `/appinstances${appInstanceId ? `/${appInstanceId}/reconnect` : ''}`, 'POST', {
      AppId: app.appId,
      ConnectionId: appInstanceId,
      AuthCode: authCode,
      RedirectUrl: GetProductionAuthReturnUrl(),
      Configs: formatConfigs(configValues),
      AuthenticationOptionId: authenticationOption ? authenticationOption.id : null
    });

    if (!appInstanceId) {
      analyticsTrack('Connected app added', { appId: app.id });
    }

    showBanner(`Application ${appInstanceId ? 'reconnected' : 'connected'} successfully`);

    return appInstanceId || response.data.data;
  } catch (err) {
    console.error(err);
    showBanner(`Error ${appInstanceId ? 'reconnecting' : 'connecting'} application`, 'error');
    throw err;
  }
}

function authorizeOAuth2Connection(app, authInfo, configValues) {
  return new Promise((resolve, reject) => {
    const w = 700;
    const h = 600;
    const y = window.outerHeight / 2 + window.screenY - h / 2;
    const x = window.outerWidth / 2 + window.screenX - w / 2;

    const url = getAuthUrl(app, authInfo, configValues);
    console.log(url);

    const addConnectionWindow = window.open(url, 'addconnection', `height=${h},width=${w},left=${x},top=${y}`);
    if (addConnectionWindow.focus) {
      addConnectionWindow.focus();
    }

    const redirectUrl = GetAuthReturnUrl();

    let pollTimeout = null;
    let pollTimer = null;
    pollTimer = setInterval(() => {
      let addConnectWindowUrl = '';
      try {
        addConnectWindowUrl = addConnectionWindow.document.URL;
      } catch (err) {
        // there will be an exception when trying to get the url when it's not on the same domain
      }

      if (addConnectWindowUrl.indexOf(redirectUrl) === 0) {
        clearInterval(pollTimer);
        clearTimeout(pollTimeout);
        addConnectionWindow.close();

        const pathInfo = getPathInfo(addConnectWindowUrl);
        if (pathInfo.params && pathInfo.params.code) {
          resolve(decodeURIComponent(pathInfo.params.code));
        } else {
          console.log(pathInfo.params);
          reject(new Error('Missing auth code'));
        }
      }
    }, 100);

    pollTimeout = setTimeout(() => {
      // timeout after 15 minutes
      clearInterval(pollTimer);
      addConnectionWindow.close();
    }, 900000);
  });
}

function getAuthUrl(app, authInfo, configValues) {
  // NetSuite requires the state to be longer than just the environment ¯\_(ツ)_/¯
  const stateConfig = { env: getEnvironment(), extra: 'a' };
  const authorizeUrl = replaceConfigVariables(authInfo.authorizeUrl, app, configValues);

  const clientId = encodeURIComponent(replaceConfigVariables(authInfo.clientId, app, configValues));
  const redirectUri = encodeURIComponent(GetProductionAuthReturnUrl());
  const state = btoa(JSON.stringify(stateConfig));
  const scope = replaceConfigVariables(authInfo.scope, app, configValues);
  const scopeUrl = scope ? `&scope=${scope}` : '';

  // Dropbox doesn't support the prompt parameter
  const appsThatDoNotSupportPrompt = ['9522abf4-caf6-499d-ac4e-68df695613e3'];

  const promptUrl = appsThatDoNotSupportPrompt.indexOf(app.appId) === -1 && authorizeUrl.indexOf('prompt=') === -1 ? '&prompt=login' : '';
  const delimiter = authorizeUrl.indexOf('?') > -1 ? '&' : '?';

  return `${authorizeUrl}${delimiter}response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}${scopeUrl}${promptUrl}`;
}

const startVariable = '${';
const endVariable = '}';

function replaceConfigVariables(value, app, configValues) {
  let newValue = value;
  const appConfigs = app.configs || [];
  if (appConfigs && newValue) {
    const authorizeUrlConfig = appConfigs.filter((x) => x.configType === 'auth');
    for (const config of authorizeUrlConfig) {
      newValue = newValue.replace(`${startVariable}${config.key}${endVariable}`, configValues[config.key] || '');
    }
  }
  return newValue;
}

function getEnvironment() {
  return process.env.NODE_ENV !== 'production' ? 'Local' : GetAppSettings().Environment;
}

export function RedirectAppAuthReturnIfNeeded() {
  if (window.location.pathname.toLowerCase() === '/app/authreturn' && window.location.search) {
    const params = getQuerystringParameters(true);
    const decodedState = atob(decodeURIComponent(params.state));
    const state = JSON.parse(decodedState);
    const env = state.env;
    const currentEnvironment = getEnvironment();

    if (env !== currentEnvironment) {
      const url = `${GetAuthReturnUrl(env)}${window.location.search}`;
      window.location.href = url;
    }
  }
}

function formatConfigs(configValues) {
  const configs = [];
  if (configValues) {
    for (const key in configValues) {
      // eslint-disable-next-line no-prototype-builtins
      if (configValues.hasOwnProperty(key)) {
        configs.push({
          Key: key,
          Value: configValues[key]
        });
      }
    }
  }
  return configs;
}
